import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useMedia } from "react-use";
import ReactCodeInput from "react-verification-code-input";
import { Modal, Space, Typography } from "antd";
import { getIn, useFormik } from "formik";
import { AuthInfo, logout } from "stores/slices/auth";
import { setHeaderTitle } from "stores/slices/layout";
import {
  doDeleteUserAccount,
  doGetUserInfo,
  doVerifyDeleteUserAccount,
  getUserDetail,
} from "stores/slices/user";

import AppButton from "components/button";
import FormInput from "components/forms/components/formInput";
import ModalForm from "components/forms/components/modalForm";

import {
  EmailIcon,
  PhoneIcon,
  TimeIcon,
  TrashIcon,
  UserIcon,
} from "../../assets/icons/index";

import UserProfileForm from "./forms/profileForm";

import styles from "../styles.module.scss";

const { confirm } = Modal;

const UserProfilePage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const authInfo = useSelector(AuthInfo);
  const userInfo = useSelector(getUserDetail);
  const below768 = useMedia("(max-width: 767.2px)");

  const [isOpenEdit, setIsOpenEdit] = useState(false);
  const [isOpenVerifyDeleteUserAccount, setIsOpenVerifyDeleteUserAccount] =
    useState(false);

  const inputItems = [
    {
      type: "text",
      key: "firstName",
      label: t("label.firstName"),
      placeholder: "label.firstName",
      icon: <UserIcon />,
      value: "Dave",
      span: 24,
      style: {
        marginBottom: 0,
        paddingTop: below768 ? 10 : 0,
        paddingBottom: below768 ? 10 : 0,
        borderTop: "0.5px solid #F4F5F7",
      },
      readOnly: true,
    },
    {
      type: "text",
      key: "lastName",
      label: t("label.lastName"),
      placeholder: "label.lastName",
      icon: <UserIcon />,
      value: "Smith",
      span: 24,
      style: {
        marginBottom: 0,
        paddingBottom: below768 ? 10 : 0,
      },
      readOnly: true,
    },
    {
      type: "text",
      key: "phoneNumber",
      label: t("label.userPhoneNumber"),
      placeholder: "text.userPhoneNumber",
      icon: <PhoneIcon />,
      value: "012 345 6789",
      span: 24,
      style: {
        marginBottom: 0,
        paddingBottom: below768 ? 10 : 0,
        borderBottom: "0.5px solid #F4F5F7",
      },
      readOnly: true,
    },
    {
      type: "text",
      key: "email",
      label: t("label.emailAddress"),
      placeholder: "label.emailAddress",
      icon: <EmailIcon />,
      value: "davesmith@jimmowing.com",
      span: 24,
      style: {
        marginBottom: 0,
        paddingBottom: below768 ? 10 : 0,
      },
      readOnly: true,
    },
    {
      type: "text",
      key: "timeZone",
      label: t("label.timezone"),
      placeholder: "label.timezone",
      icon: <TimeIcon />,
      span: 24,
      style: {
        marginBottom: 0,
        paddingBottom: below768 ? 10 : 0,
        borderBottom: "0.5px solid #F4F5F7",
      },
      readOnly: true,
    },
  ];

  const handleConfirmDeleteUserAccount = () => {
    confirm({
      title: t("user.userProfile.confirmDeleteAccountTitle"),
      content: t("user.userProfile.confirmDeleteAccountMsg"),
      okText: t("button.delete"),
      okType: "danger",
      cancelText: t("button.cancel"),
      async onOk() {
        await handleDeleteUser();
      },
      onCancel() {},
    });
  };

  const handleDeleteUser = async () => {
    if (userInfo && userInfo.id) {
      const { payload } = await dispatch(
        doDeleteUserAccount({ id: userInfo.id })
      );

      if (payload && payload.status === 200) {
        setIsOpenVerifyDeleteUserAccount(true);
      }
    }
  };

  const handleVerifyDeleteUserAccount = async value => {
    if (value.length === 6) {
      const { payload } = await dispatch(
        doVerifyDeleteUserAccount({ code: value })
      );
      if (payload && payload.status === 200) {
        setIsOpenVerifyDeleteUserAccount(false);
        await dispatch(logout());
      }
    }
  };

  const handleForm = useFormik({
    initialValues: {
      firstName: userInfo?.firstName || "-",
      lastName: userInfo?.lastName || "-",
      phoneNumber: userInfo?.phoneNumber || "-",
      email: userInfo?.email || "-",
      timeZone: userInfo?.timeZone || "-",
    },
  });

  const { values, setFieldValue, setValues, handleReset } = handleForm;

  useEffect(() => {
    dispatch(setHeaderTitle("userDetails"));
  }, []);

  useEffect(() => {
    if (authInfo.id) {
      dispatch(doGetUserInfo(authInfo.id));
    }
  }, [authInfo]);

  useEffect(() => {
    if (userInfo) {
      handleReset();
      setValues({
        firstName: userInfo?.firstName || "-",
        lastName: userInfo?.lastName || "-",
        phoneNumber: userInfo?.phoneNumber || "-",
        email: userInfo?.email || "-",
        timeZone: userInfo?.timeZone || "-",
      });
    }
  }, [userInfo]);

  return (
    <>
      <div className={styles.profilePage}>
        <div className={styles.profileHeader}>
          <Typography
            style={{
              fontSize: "36px",
              fontWeight: 500,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: 800,
              maxWidth: "100%",
            }}
          >
            {userInfo?.firstName || userInfo?.lastName
              ? userInfo?.firstName + " " + userInfo?.lastName
              : userInfo?.email}
          </Typography>
        </div>
        <div className={styles.profileContent}>
          <div>
            <Typography
              style={{
                fontSize: "20px",
                fontWeight: 500,
                borderBottom: "3px solid #5162F3",
                color: "#5162F3",
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              {t("label.userDetails")}
            </Typography>
          </div>
          <Space
            direction="vertical"
            size="small"
            style={{
              display: "flex",
            }}
          >
            <FormInput
              items={inputItems.map(item => {
                item.label = t(item.label);
                if (item.key) {
                  item.value = getIn(values, item.key);
                }
                return { ...item };
              })}
              form={handleForm}
              onChange={setFieldValue}
            />
            <div className={styles.customButtonRight}>
              <div>
                <AppButton
                  label={t("button.deleteUserAccount")}
                  color={"err"}
                  type="submit"
                  onClick={() => handleConfirmDeleteUserAccount()}
                />
              </div>
              <div style={{ marginLeft: 20 }}>
                <AppButton
                  label={t("label.edit")}
                  color={"primary"}
                  type="submit"
                  onClick={() => setIsOpenEdit(true)}
                />
              </div>
            </div>
          </Space>
        </div>
        <UserProfileForm
          setIsOpenEdit={setIsOpenEdit}
          isOpenEdit={isOpenEdit}
        />
      </div>
      <ModalForm
        onCancel={() => setIsOpenVerifyDeleteUserAccount(false)}
        onSubmit={() => setIsOpenVerifyDeleteUserAccount(false)}
        destroyOnClose={true}
        isOpen={isOpenVerifyDeleteUserAccount}
        labelSubmit={t("label.save")}
        customWidth={500}
        footer={false}
        maskClosable={false}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
          }}
        >
          <div>
            <TrashIcon width={30} height={30} />
          </div>
          <Typography.Title level={4} style={{ marginLeft: 10 }}>
            {t("user.userProfile.deleteUserAccount")}
          </Typography.Title>
        </div>
        <div style={{ marginBottom: 12 }}>
          <Typography.Text>
            {t("user.userProfile.enterVerifyCodeDeleteAccount")}
          </Typography.Text>
        </div>
        <ReactCodeInput
          type={"number"}
          onComplete={val => handleVerifyDeleteUserAccount(val)}
          className="twoFactorAuthInput"
        />
        <div
          style={{ marginTop: 12, display: "flex", justifyContent: "center" }}
        >
          <AppButton
            label={t("button.cancel")}
            color={"grey"}
            type="submit"
            onClick={() => setIsOpenVerifyDeleteUserAccount(false)}
          />
        </div>
      </ModalForm>
    </>
  );
};

export default UserProfilePage;
